// apps/workflow/src/pages/admin/AdminFreelancers.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  Download,
  Clock,
  UserPlus,
  Calendar,
  Mail,
  Phone,
  X,
  FileText,
  Image,
  Edit,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

const AdminFreelancers = () => {
  const { user: currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingRole, setEditingRole] = useState(false);
  const [newRole, setNewRole] = useState('');

  const ALL_ROLES = [
    'admin', 'super_admin', 'manager', 'designer', 'freelancer',
    'applied_freelancer', 'sales', 'hr', 'accounts', 'accountant',
    'content_writer', 'client', 'user', 'directors',
    'construction_partners', 'interior_partners'
  ];

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const records = await pb.collection('freelancer_applications').getFullList({
        sort: '-created',
        expand: 'user',
        requestKey: null,
      });
      
      // For each record, if expand.user is missing, fetch the user by email
      const enriched = await Promise.all(
        records.map(async (app) => {
          if (!app.expand?.user && app.email) {
            try {
              const users = await pb.collection('users').getFullList({
                filter: `email = "${app.email}"`,
                limit: 1,
              });
              if (users.length > 0) {
                app.expand = app.expand || {};
                app.expand.user = users[0];
              }
            } catch (err) {
              console.warn(`Could not fetch user for ${app.email}`, err);
            }
          }
          return app;
        })
      );
      
      setApplications(enriched);
    } catch (error) {
      if (!error.isAbort) {
        console.error('Error fetching applications:', error);
        toast.error('Failed to load applications');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchApplications();
    setRefreshing(false);
    toast.success('Applications refreshed');
  };

  const filteredApps = applications.filter((app) => {
    const matchesSearch =
      app.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.phone?.includes(searchTerm) ||
      app.expand?.user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // ─── APPROVE ────────────────────────────────────────────────
  const handleApprove = async (id) => {
    try {
      const app = await pb.collection('freelancer_applications').getOne(id, { expand: 'user' });

      await pb.collection('freelancer_applications').update(id, {
        status: 'approved',
        approvedBy: currentUser.id,
        approvedAt: new Date().toISOString(),
      });

      let userId = null;

      // Try to get user ID from relation or email
      if (app.user) {
        userId = app.user;
      } else if (app.expand?.user?.id) {
        userId = app.expand.user.id;
      } else {
        // Fallback: search by email
        const users = await pb.collection('users').getFullList({
          filter: `email = "${app.email}"`,
        });
        if (users.length > 0) {
          userId = users[0].id;
        } else {
          toast.warning(`⚠️ No user account found for ${app.name}`);
          fetchApplications();
          return;
        }
      }

      if (userId) {
        await pb.collection('users').update(userId, {
          role: 'freelancer',
          subscriptionStart: null,
          subscriptionEnd: null,
        });
        toast.success(`✅ ${app.name} approved. Role changed to freelancer. They can now subscribe.`);
      } else {
        toast.warning(`⚠️ Could not identify user for ${app.name}`);
      }

      fetchApplications();
    } catch (error) {
      console.error('❌ Approval error:', error);
      toast.error('Failed to approve: ' + error.message);
    }
  };

  // ─── REJECT ──────────────────────────────────────────────────
  const handleReject = async (id) => {
    try {
      await pb.collection('freelancer_applications').update(id, {
        status: 'rejected',
        approvedBy: currentUser.id,
        approvedAt: new Date().toISOString(),
      });
      toast.success('Application rejected');
      fetchApplications();
    } catch (error) {
      toast.error('Failed to reject: ' + error.message);
    }
  };

  // ─── EDIT ROLE ──────────────────────────────────────────────
  const handleEditRole = async () => {
    if (!selectedApp || !selectedApp.user) {
      toast.error('No user linked to this application');
      return;
    }
    try {
      await pb.collection('users').update(selectedApp.user, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      setEditingRole(false);
      fetchApplications();
      const refreshedApp = await pb.collection('freelancer_applications').getOne(selectedApp.id, { expand: 'user' });
      setSelectedApp(refreshedApp);
    } catch (error) {
      console.error('Role update error:', error);
      toast.error('Failed to update role');
    }
  };

  const handleViewDetail = (app) => {
    setSelectedApp(app);
    setEditingRole(false);
    setNewRole(app.expand?.user?.role || '');
    setShowDetailModal(true);
  };

  const handleDownload = (app, field) => {
    const url = pb.files.getUrl(app, app[field]);
    window.open(url, '_blank');
  };

  const total = applications.length;
  const pending = applications.filter((a) => a.status === 'pending').length;
  const approved = applications.filter((a) => a.status === 'approved').length;
  const rejected = applications.filter((a) => a.status === 'rejected').length;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-500 font-medium">Loading applications...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-indigo-600" />
            Freelancer Approvals
          </h1>
          <p className="text-sm text-slate-500">Review and approve freelancer applications</p>
        </div>
        <button onClick={handleRefresh} disabled={refreshing} className="p-2 text-slate-400 hover:text-indigo-600 transition rounded-lg hover:bg-indigo-50 disabled:opacity-50">
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard title="Total" value={total} icon={Users} color="indigo" />
        <StatCard title="Pending" value={pending} icon={Clock} color="amber" />
        <StatCard title="Approved" value={approved} icon={CheckCircle} color="green" />
        <StatCard title="Rejected" value={rejected} icon={XCircle} color="red" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 bg-white p-3 rounded-xl shadow-sm border border-slate-200">
        <div className="relative flex-1 min-w-[150px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by name, email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 outline-none"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Phone</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Application Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Current Role</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Submitted</th>
                <th className="text-center px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredApps.length === 0 ? (
                <tr><td colSpan="7" className="text-center py-12 text-slate-400">No applications found.</td></tr>
              ) : (
                filteredApps.map((app) => (
                  <tr key={app.id} className="hover:bg-slate-50/50 transition">
                    <td className="px-6 py-3 font-medium text-slate-800">
                      {app.name}
                      {app.expand?.user && <span className="ml-2 text-xs text-slate-400">(User: {app.expand.user.name})</span>}
                    </td>
                    <td className="px-6 py-3 text-slate-600">{app.email}</td>
                    <td className="px-6 py-3 text-slate-600">{app.phone || '—'}</td>
                    <td className="px-6 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${app.status === 'approved' ? 'bg-green-100 text-green-800' : app.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>
                        {app.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-6 py-3">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">
                        {app.expand?.user?.role || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-slate-500">{new Date(app.created).toLocaleDateString()}</td>
                    <td className="px-6 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleViewDetail(app)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded transition" title="View Details">
                          <Eye className="w-4 h-4" />
                        </button>
                        {app.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(app.id)} className="p-1 text-green-600 hover:bg-green-50 rounded transition" title="Approve (set as freelancer, no subscription)">
                              <CheckCircle className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleReject(app.id)} className="p-1 text-red-600 hover:bg-red-50 rounded transition" title="Reject">
                              <XCircle className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-slate-800">Application Details</h3>
              <button onClick={() => setShowDetailModal(false)} className="text-slate-400 hover:text-slate-600 transition">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><p className="text-sm font-medium text-slate-600">Name</p><p className="text-sm text-slate-800">{selectedApp.name}</p></div>
              <div><p className="text-sm font-medium text-slate-600">Email</p><p className="text-sm text-slate-800">{selectedApp.email}</p></div>
              <div><p className="text-sm font-medium text-slate-600">Phone</p><p className="text-sm text-slate-800">{selectedApp.phone || '—'}</p></div>
              <div><p className="text-sm font-medium text-slate-600">Application Status</p><span className={`px-2 py-1 text-xs font-medium rounded-full ${selectedApp.status === 'approved' ? 'bg-green-100 text-green-800' : selectedApp.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}`}>{selectedApp.status || 'pending'}</span></div>
              <div><p className="text-sm font-medium text-slate-600">Current Role</p><span className="px-2 py-1 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800">{selectedApp.expand?.user?.role || 'N/A'}</span></div>
              <div className="md:col-span-2"><p className="text-sm font-medium text-slate-600">Experience</p><p className="text-sm text-slate-700 whitespace-pre-wrap">{selectedApp.experience || 'No experience details provided.'}</p></div>
            </div>

            {/* Role Edit Section */}
            <div className="mt-6 border-t border-slate-200 pt-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-slate-700">Edit User Role:</span>
                {editingRole ? (
                  <>
                    <select value={newRole} onChange={(e) => setNewRole(e.target.value)} className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none">
                      {ALL_ROLES.map((role) => <option key={role} value={role}>{role}</option>)}
                    </select>
                    <button onClick={handleEditRole} className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition flex items-center gap-1"><Save className="w-4 h-4" /> Save</button>
                    <button onClick={() => setEditingRole(false)} className="px-3 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-sm hover:bg-slate-50 transition">Cancel</button>
                  </>
                ) : (
                  <button onClick={() => setEditingRole(true)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition flex items-center gap-1"><Edit className="w-4 h-4" /> Edit Role</button>
                )}
              </div>
            </div>

            {/* Files */}
            <div className="mt-4 border-t border-slate-200 pt-4">
              <h4 className="font-semibold text-slate-700 mb-2">Uploaded Files</h4>
              <div className="flex flex-wrap gap-4">
                {selectedApp.portfolio && <button onClick={() => handleDownload(selectedApp, 'portfolio')} className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition"><FileText className="w-4 h-4" /> Portfolio <Download className="w-4 h-4 ml-auto" /></button>}
                {selectedApp.paymentScreenshot && <button onClick={() => handleDownload(selectedApp, 'paymentScreenshot')} className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition"><Image className="w-4 h-4" /> Payment Screenshot <Download className="w-4 h-4 ml-auto" /></button>}
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-200">
              <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition">Close</button>
              {selectedApp.status === 'pending' && (
                <>
                  <button onClick={() => { handleApprove(selectedApp.id); setShowDetailModal(false); }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">Approve</button>
                  <button onClick={() => { handleReject(selectedApp.id); setShowDetailModal(false); }} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition">Reject</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── StatCard ──────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = { indigo: 'bg-indigo-50 text-indigo-600', green: 'bg-green-50 text-green-600', amber: 'bg-amber-50 text-amber-600', red: 'bg-red-50 text-red-600', blue: 'bg-blue-50 text-blue-600', purple: 'bg-purple-50 text-purple-600' };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:shadow-md transition">
      <div className="flex items-center justify-between">
        <div><p className="text-sm text-slate-500">{title}</p><p className="text-2xl font-bold text-slate-800 mt-1">{value}</p></div>
        <div className={`p-3 rounded-full ${colors[color] || 'bg-slate-50 text-slate-600'}`}><Icon className="w-5 h-5" /></div>
      </div>
    </div>
  );
};

export default AdminFreelancers;